// Kaart-view. Eén persistent MapLibre-instance, OSM-raster.
//
// Pinnen zijn hierarchisch gegroepeerd via node.parentId:
//   - Onder EXPAND_ZOOM tonen we alleen parents-met-kinderen; kinderen worden
//     gerepresenteerd door een badge met het aantal op de parent-pin.
//   - Klik op een parent expandeert die ene cluster (pop-animatie).
//   - Voldoende inzoomen expandeert automatisch alle clusters.
//   - Onder de threshold weer zoomen wist de handmatige expansies — anders
//     loopt de kaart vol op grote afstanden.
//
// Markers worden diff-gebaseerd ververst (markerById Map) zodat ongewijzigde
// pinnen niet flikkeren tijdens re-render.

import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { getStore, setFilter, subscribe, toggleFavorite, setGameState } from '../store.js'
import { filterNodes } from '../data.js'
import { icon, hasIcon } from '../icons.js'
import { setTitle, navigate } from '../main.js'
import { setState, discoverPlaces } from '../api.js'
import { CHALET as CHALET_FALLBACK } from '../config.js'
import { escapeHtml } from '../util.js'
import { fetchImage } from '../images.js'

const FALLBACK_CENTER = [12.9347, 50.3585]
const INITIAL_ZOOM = 9.2
const EXPAND_ZOOM = 13     // boven dit zoom-niveau klappen alle clusters open

function getMapCenter(ctx) {
  const c = ctx.data?.meta?.homeBaseCoord
  return c ? [c.lng, c.lat] : FALLBACK_CENTER
}

let wrap = null
let mapContainer = null
let map = null
let markerById = new Map()      // nodeId → { marker, el, signature }
let hikeMarkerById = new Map()  // hikeId → { marker, el }
let chaletMarker = null
let popup = null
let sheetEl = null
let filterPanelOpen = false
let storeUnsub = null
let expandedParents = new Set() // handmatig opengeklapte cluster-parents
let lastZoom = INITIAL_ZOOM
let zoomListenerCtx = null      // ctx voor de zoom-listener (zelfde over re-mounts)
let isInitialRender = true      // skip pop-animaties bij allereerste render
let discoveryMarkers = []       // AI-ontdekte plekken (tijdelijke markers)
let mapRegionId = null

const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}

function buildWrap() {
  if (wrap) return wrap
  wrap = document.createElement('div')
  wrap.className = 'map-wrap'
  wrap.innerHTML = `
    <div id="maplibre-container"></div>
    <div class="map-controls" id="map-controls"></div>
    <div class="filter-panel" id="filter-panel" hidden></div>
    <button class="map-fab discover-fab" id="discover-btn" type="button" title="Zoek meer in dit gebied">
      ${icon('sparkles')}
    </button>
    <div class="map-sheet">
      <div class="map-sheet-handle"></div>
      <button class="map-sheet-close">${icon('x')}</button>
      <div class="map-sheet-body"></div>
    </div>
  `
  mapContainer = wrap.querySelector('#maplibre-container')
  sheetEl = wrap.querySelector('.map-sheet')
  sheetEl.querySelector('.map-sheet-close').addEventListener('click', closeSheet)
  initSheetDrag(sheetEl)
  return wrap
}

function ensureMap(ctx) {
  if (map) {
    zoomListenerCtx = ctx
    const rid = ctx.region?.id
    if (rid && rid !== mapRegionId) {
      mapRegionId = rid
      markerById.forEach(m => m.marker.remove())
      markerById.clear()
      expandedParents.clear()
      map.jumpTo({ center: getMapCenter(ctx), zoom: INITIAL_ZOOM })
    }
    return map
  }
  mapRegionId = ctx.region?.id
  map = new maplibregl.Map({
    container: mapContainer,
    style: OSM_STYLE,
    center: getMapCenter(ctx),
    zoom: INITIAL_ZOOM,
    minZoom: 7,
    maxZoom: 17,
    attributionControl: false,
  })
  map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
  const geoControl = new maplibregl.GeolocateControl({
    positionOptions: { enableHighAccuracy: false },
    trackUserLocation: false,
  })
  map.addControl(geoControl, 'top-right')
  geoControl.on('error', (e) => {
    if (e.code === 1) {
      let toast = wrap.querySelector('.geo-denied-toast')
      if (!toast) {
        toast = document.createElement('div')
        toast.className = 'geo-denied-toast'
        toast.innerHTML = `<p><strong>Locatie geblokkeerd</strong></p><p>Tik op het slot-icoon in de adresbalk, stel Locatie in op "Toestaan" en herlaad de pagina.</p>`
        wrap.querySelector('.map-container').appendChild(toast)
      }
      toast.classList.add('visible')
      setTimeout(() => toast.classList.remove('visible'), 6000)
    }
  })

  map.on('click', () => closeSheet())

  zoomListenerCtx = ctx
  map.on('zoom', () => {
    const z = map.getZoom()
    // Cluster-overgang: zodra zoom de threshold kruist, direct re-renderen
    // zodat kinderen niet zichtbaar blijven tijdens zoom-animatie
    const crossed = (lastZoom >= EXPAND_ZOOM && z < EXPAND_ZOOM)
                 || (lastZoom < EXPAND_ZOOM && z >= EXPAND_ZOOM)
    if (crossed) {
      if (z < EXPAND_ZOOM) expandedParents.clear()
      lastZoom = z
      if (zoomListenerCtx) renderMarkers(zoomListenerCtx)
    }
  })
  map.on('zoomend', () => {
    const z = map.getZoom()
    lastZoom = z
    if (zoomListenerCtx) renderMarkers(zoomListenerCtx)
  })

  window.addEventListener('center-hike', (e) => {
    if (!map) return
    const { coord } = e.detail
    map.flyTo({ center: [coord.lng, coord.lat], zoom: 14, duration: 800 })
  })

  return map
}

// ---------- Filter-chips ----------
function renderControls(ctx) {
  const { filters } = getStore()
  const controls = wrap.querySelector('#map-controls')
  controls.innerHTML = ''

  const filterBtn = document.createElement('button')
  filterBtn.className = 'chip' + (filterPanelOpen ? ' active' : '')
  filterBtn.type = 'button'
  filterBtn.innerHTML = `${icon('sliders')} Filters`
  filterBtn.addEventListener('click', () => {
    filterPanelOpen = !filterPanelOpen
    renderControls(ctx)
    renderFilterPanel(ctx)
  })
  controls.appendChild(filterBtn)
}

function renderFilterPanel(ctx) {
  const panel = wrap.querySelector('#filter-panel')
  if (!filterPanelOpen) { panel.hidden = true; return }
  panel.hidden = false

  const { data } = ctx
  const { filters } = getStore()

  const catAll = new Set(data.meta.categories.map(c => c.id))
  const eraAll = new Set(data.meta.eras.map(e => e.id))

  const closeHtml = `
    <div class="filter-header">
      <span class="filter-title">Filters</span>
      <button class="filter-close" id="filter-close">${icon('x')}</button>
    </div>
  `

  const tierHtml = `
    <h3>Type bestemming</h3>
    <div class="filter-row">
      ${data.meta.tiers.map(t => {
        const active = filters.tiers?.has(t.id) ?? t.defaultVisible
        return `<button class="chip toggle tier-${t.id} ${active ? 'active' : ''}" data-tier="${t.id}">${t.label}</button>`
      }).join('')}
      <button class="chip toggle favorites ${filters.onlyFavorites ? 'active' : ''}" id="fav-filter">
        ${icon('heart')} Favorieten
      </button>
    </div>
  `

  const starHtml = `
    <h3>Minimumsterren</h3>
    <div class="filter-row">
      <button class="chip toggle ${!filters.stars || filters.stars === 0 ? 'active' : ''}" data-stars="0">Alle</button>
      ${[1, 2, 3, 4, 5].map(n => `
        <button class="chip toggle ${filters.stars === n ? 'active' : ''}" data-stars="${n}">
          ${'★'.repeat(n)}${'☆'.repeat(5 - n)}
        </button>`).join('')}
    </div>
  `

  function groupHeader(label, key) {
    const cur = filters[key]
    const allOn = !cur
    const noneOn = cur && cur.size === 0
    return `
      <h3>
        ${label}
        <span class="group-actions">
          <button class="mini-chip ${allOn ? 'active' : ''}" data-set-all="${key}">Alle</button>
          <button class="mini-chip ${noneOn ? 'active' : ''}" data-set-none="${key}">Geen</button>
        </span>
      </h3>`
  }

  const catHtml = `
    ${groupHeader('Categorieën', 'categories')}
    <div class="filter-row">
      ${data.meta.categories.map(c => {
        const active = !filters.categories || filters.categories.has(c.id)
        return `<button class="chip toggle cat-${c.id} ${active ? 'active' : ''}" data-cat="${c.id}">
          ${hasIcon(c.icon) ? icon(c.icon) : ''} ${c.label}
        </button>`
      }).join('')}
    </div>
  `

  const eraHtml = `
    ${groupHeader('Tijdperk', 'eras')}
    <div class="filter-row">
      ${data.meta.eras.map(e => {
        const active = !filters.eras || filters.eras.has(e.id)
        return `<button class="chip toggle ${active ? 'active' : ''}" data-era="${e.id}" style="${active ? `background:${e.color};color:#fff;border-color:${e.color}` : ''}">
          ${e.label}
        </button>`
      }).join('')}
    </div>
  `

  const hikeHtml = `
    <h3>Routes</h3>
    <div class="filter-row">
      <button class="chip toggle hike-toggle ${filters.showHikes ? 'active' : ''}" id="hike-filter">
        ${icon('footprints')} Wandelroutes tonen
      </button>
    </div>
  `

  panel.innerHTML = closeHtml + tierHtml + starHtml + catHtml + eraHtml + hikeHtml + `
    <div class="filter-row" style="margin-top:14px;justify-content:flex-end">
      <button class="chip" id="reset-filters">${icon('rotate')} Alle filters wissen</button>
    </div>
  `

  panel.querySelector('#filter-close')?.addEventListener('click', () => {
    filterPanelOpen = false
    renderControls(ctx)
    renderFilterPanel(ctx)
  })

  panel.querySelectorAll('[data-tier]').forEach(b => {
    b.addEventListener('click', () => {
      const next = new Set(filters.tiers ?? new Set())
      if (next.has(b.dataset.tier)) next.delete(b.dataset.tier); else next.add(b.dataset.tier)
      setFilter('tiers', next)
    })
  })

  panel.querySelector('#fav-filter')?.addEventListener('click', () => {
    setFilter('onlyFavorites', !filters.onlyFavorites)
  })

  panel.querySelector('#hike-filter')?.addEventListener('click', () => {
    setFilter('showHikes', !filters.showHikes)
  })

  panel.querySelectorAll('[data-stars]').forEach(b => {
    b.addEventListener('click', () => setFilter('stars', Number(b.dataset.stars)))
  })

  panel.querySelectorAll('[data-cat]').forEach(b => {
    b.addEventListener('click', () => {
      const id = b.dataset.cat
      const cur = new Set(filters.categories ?? catAll)
      if (cur.has(id)) cur.delete(id); else cur.add(id)
      setFilter('categories', cur.size === catAll.size ? null : cur)
    })
  })

  panel.querySelectorAll('[data-era]').forEach(b => {
    b.addEventListener('click', () => {
      const id = b.dataset.era
      const cur = new Set(filters.eras ?? eraAll)
      if (cur.has(id)) cur.delete(id); else cur.add(id)
      setFilter('eras', cur.size === eraAll.size ? null : cur)
    })
  })

  panel.querySelectorAll('[data-set-all]').forEach(b => {
    b.addEventListener('click', () => setFilter(b.dataset.setAll, null))
  })
  panel.querySelectorAll('[data-set-none]').forEach(b => {
    b.addEventListener('click', () => setFilter(b.dataset.setNone, new Set()))
  })

  panel.querySelector('#reset-filters')?.addEventListener('click', () => {
    const defaults = new Set(data.meta.tiers.filter(t => t.defaultVisible).map(t => t.id))
    setFilter('tiers', defaults)
    setFilter('stars', 0)
    setFilter('eras', null)
    setFilter('categories', null)
    setFilter('showHikes', false)
    setFilter('onlyFavorites', false)
  })
}

// ---------- View-berekening (clustering) ----------
function computeView(ctx) {
  const { data, indexes } = ctx
  const store = getStore()
  const favorites = store.game.favorites
  const visible = filterNodes(data, indexes, store.filters, favorites)
  const zoom = map.getZoom()

  // Aantal kinderen per parent dat ná de filters nog meedoet
  const parentChildCount = new Map()
  for (const n of visible) {
    if (n.parentId && indexes.nodesById.has(n.parentId)) {
      parentChildCount.set(n.parentId, (parentChildCount.get(n.parentId) || 0) + 1)
    }
  }

  const isExpanded = (parentId) => {
    if (zoom >= EXPAND_ZOOM) return true
    return expandedParents.has(parentId)
  }

  const filtered = visible.filter(n => {
    if (n.parentId && parentChildCount.has(n.parentId)) {
      return isExpanded(n.parentId)
    }
    return true
  })

  return { visible, filtered, parentChildCount, isExpanded, zoom, favorites }
}

function makeSignature(node, view) {
  const fav = view.favorites[node.id] ? 'F' : ''
  const childCount = view.parentChildCount.get(node.id) || 0
  const expanded = childCount > 0 && view.isExpanded(node.id) ? 'X' : ''
  return `${node.visit || ''}|${node.kind || ''}|${node.category}|${fav}|${childCount}|${expanded}`
}

function applyElState(el, node, view, cat) {
  const tierClass = `tier-${node.visit || 'bestemming'}`
  const catClass = `cat-${node.category || 'gebouw'}`
  const kindClass = node.kind === 'town' ? 'is-town' : ''
  const favClass = view.favorites[node.id] ? 'favorite' : ''
  const childCount = view.parentChildCount.get(node.id) || 0
  const hasKidsClass = childCount > 0 ? 'has-children' : ''
  const expandedClass = (childCount > 0 && view.isExpanded(node.id)) ? 'expanded' : ''

  const mlClasses = []
  for (const c of el.classList) {
    if (c.startsWith('maplibregl-')) mlClasses.push(c)
  }
  el.className = ['map-pin', tierClass, catClass, kindClass, favClass, hasKidsClass, expandedClass, ...mlClasses]
    .filter(Boolean).join(' ')
  el.title = node.name

  if (childCount > 0) {
    el.dataset.count = String(childCount)
  } else if ('count' in el.dataset) {
    delete el.dataset.count
  }

  if (!el.innerHTML) {
    el.innerHTML = (cat?.icon && hasIcon(cat.icon)) ? icon(cat.icon) : icon('sparkles')
  }
}

// ---------- Marker-rendering (diff-based) ----------
function renderMarkers(ctx) {
  if (!map) return
  const view = computeView(ctx)
  const visibleIds = new Set(view.filtered.map(n => n.id))

  // Verwijder markers die niet meer in beeld zitten
  for (const [id, entry] of markerById) {
    if (!visibleIds.has(id)) {
      entry.marker.remove()
      markerById.delete(id)
    }
  }

  // Voeg nieuwe toe / werk gewijzigde bij
  for (const node of view.filtered) {
    if (!node.coord) continue
    const cat = ctx.indexes.categoryById.get(node.category)
    const sig = makeSignature(node, view)
    const existing = markerById.get(node.id)

    if (existing) {
      if (existing.signature !== sig) {
        applyElState(existing.el, node, view, cat)
        existing.signature = sig
      }
      continue
    }

    // Nieuwe marker
    const el = document.createElement('div')
    applyElState(el, node, view, cat)

    // Pop-animatie alleen voor markers die op een al bestaande kaart verschijnen
    if (!isInitialRender) {
      el.classList.add('appearing')
      el.addEventListener('animationend', () => el.classList.remove('appearing'), { once: true })
    }

    el.addEventListener('click', (e) => onPinClick(e, node, ctx))

    const m = new maplibregl.Marker({ element: el })
      .setLngLat([node.coord.lng, node.coord.lat])
      .addTo(map)

    markerById.set(node.id, { marker: m, el, signature: sig })
  }

  isInitialRender = false
  ensureChaletMarker(ctx)
  renderHikeMarkers(ctx)
}

function onPinClick(e, node, ctx) {
  e.stopPropagation()
  const view = computeView(ctx)
  const childCount = view.parentChildCount.get(node.id) || 0

  // Klik op een collapsed cluster expandeert; dichtklappen gebeurt via een
  // knop in de popup (of door uit te zoomen onder de threshold)
  if (childCount > 0 && view.zoom < EXPAND_ZOOM && !expandedParents.has(node.id)) {
    expandedParents.add(node.id)
    renderMarkers(ctx)
  }
  openPopup(node, ctx)
}

function ensureChaletMarker(ctx) {
  const hb = ctx.data?.meta?.homeBase ?? CHALET_FALLBACK
  if (!hb?.coord) return

  if (chaletMarker) {
    const cur = chaletMarker._hbKey
    const key = `${hb.coord.lat},${hb.coord.lng}`
    if (cur === key) return
    chaletMarker.remove()
    chaletMarker = null
  }

  const el = document.createElement('div')
  el.className = 'map-pin chalet'
  el.title = hb.name
  el.innerHTML = icon('home')

  chaletMarker = new maplibregl.Marker({ element: el })
    .setLngLat([hb.coord.lng, hb.coord.lat])
    .addTo(map)
  chaletMarker._hbKey = `${hb.coord.lat},${hb.coord.lng}`

  el.addEventListener('click', (e) => {
    e.stopPropagation()
    if (popup) popup.remove()
    closeSheet()
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${hb.coord.lat},${hb.coord.lng}`
    const chaletHtml = `
      <div class="pin-popup">
        <div class="pin-cat">${escapeHtml(hb.note || 'Thuisbasis')}</div>
        <h3>${escapeHtml(hb.name)}</h3>
        <p>${escapeHtml(hb.address)}</p>
        <a href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener">Open in Google Maps ›</a>
      </div>
    `
    if (isMobile()) {
      showSheet(chaletHtml, [hb.coord.lng, hb.coord.lat])
    } else {
      popup = new maplibregl.Popup({ closeButton: true, maxWidth: '260px', offset: 18 })
        .setLngLat([hb.coord.lng, hb.coord.lat])
        .setHTML(chaletHtml)
        .addTo(map)
    }
  })
}

// ---------- Hike-markers ----------
function renderHikeMarkers(ctx) {
  const show = getStore().filters.showHikes
  if (!show) {
    for (const [id, entry] of hikeMarkerById) {
      entry.marker.remove()
    }
    hikeMarkerById.clear()
    return
  }

  const hikes = ctx.data.hikes ?? []
  const currentIds = new Set(hikes.map(h => h.id))

  // Remove stale
  for (const [id, entry] of hikeMarkerById) {
    if (!currentIds.has(id)) {
      entry.marker.remove()
      hikeMarkerById.delete(id)
    }
  }

  // Add new
  for (const hike of hikes) {
    if (!hike.coord || hikeMarkerById.has(hike.id)) continue

    const el = document.createElement('div')
    el.className = 'map-pin hike-pin'
    el.innerHTML = icon('footprints')

    el.addEventListener('click', (e) => {
      e.stopPropagation()
      openHikePopup(hike, ctx)
    })

    const m = new maplibregl.Marker({ element: el })
      .setLngLat([hike.coord.lng, hike.coord.lat])
      .addTo(map)

    hikeMarkerById.set(hike.id, { marker: m, el })
  }
}

// ---------- Bottom sheet (mobiel) ----------
const isMobile = () => window.matchMedia('(max-width: 639px)').matches
let sheetOpenedAt = 0

function closeSheet(force) {
  if (!sheetEl) return
  if (!force && Date.now() - sheetOpenedAt < 300) return
  sheetEl.classList.remove('open')
  sheetEl.style.maxHeight = ''
}

function showSheet(html, lngLat) {
  const body = sheetEl.querySelector('.map-sheet-body')
  body.innerHTML = html
  sheetEl.style.maxHeight = ''
  void sheetEl.offsetHeight
  sheetEl.classList.add('open')
  sheetOpenedAt = Date.now()

  if (lngLat && map) {
    setTimeout(() => {
      const point = map.project(lngLat)
      const sheetH = sheetEl.getBoundingClientRect().height
      const mapH = mapContainer.getBoundingClientRect().height
      const targetY = (mapH - sheetH) * 0.45
      const dy = point.y - targetY
      if (Math.abs(dy) > 10) map.panBy([0, dy], { duration: 400 })
    }, 50)
  }
  return body
}

function initSheetDrag(el) {
  const handle = el.querySelector('.map-sheet-handle')
  let startY = 0, startH = 0, dragging = false

  handle.addEventListener('touchstart', (e) => {
    dragging = true
    startY = e.touches[0].clientY
    startH = el.getBoundingClientRect().height
    el.style.transition = 'none'
  }, { passive: true })

  handle.addEventListener('touchmove', (e) => {
    if (!dragging) return
    const dy = startY - e.touches[0].clientY
    const mapH = mapContainer.getBoundingClientRect().height
    const newH = Math.max(20, Math.min(mapH * 0.85, startH + dy))
    el.style.maxHeight = newH + 'px'
  }, { passive: true })

  const onEnd = () => {
    if (!dragging) return
    dragging = false
    el.style.transition = ''
    const h = el.getBoundingClientRect().height
    if (h < 80) closeSheet(true)
  }
  handle.addEventListener('touchend', onEnd)
  handle.addEventListener('touchcancel', onEnd)
}

function wireHikeEvents(container, hike) {
  container.querySelectorAll('[data-hike-nav]').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault()
      if (popup) popup.remove()
      closeSheet()
      navigate(`#/hikes/${a.getAttribute('data-hike-nav')}`)
    })
  })
}

function openHikePopup(hike, ctx) {
  if (popup) popup.remove()
  closeSheet()

  const diffColors = { makkelijk: 'var(--warm)', middel: 'var(--amber-600)', zwaar: 'var(--accent)' }
  const diffColor = diffColors[hike.difficulty] || 'var(--muted)'
  const durationStr = hike.durationMin ? `${Math.floor(hike.durationMin / 60)}u${String(hike.durationMin % 60).padStart(2, '0')}` : ''

  const html = `
    <div class="pin-popup hike-popup">
      <div class="pin-cat" style="color:${diffColor}">${escapeHtml(hike.difficulty)} · ${hike.distanceKm} km</div>
      <h3>${escapeHtml(hike.name)}</h3>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin:4px 0 8px">
        ${hike.ascentM ? `<span class="badge">↑ ${hike.ascentM} m</span>` : ''}
        ${durationStr ? `<span class="badge">${durationStr}</span>` : ''}
        <span class="badge">${hike.loop ? 'Rondwandeling' : 'Heen & terug'}</span>
      </div>
      <div class="popup-actions">
        <a href="#/hikes/${hike.id}" data-hike-nav="${escapeHtml(hike.id)}">Details ›</a>
      </div>
    </div>
  `

  if (isMobile()) {
    const body = showSheet(html, [hike.coord.lng, hike.coord.lat])
    wireHikeEvents(body, hike)
    return
  }

  popup = new maplibregl.Popup({ closeButton: true, maxWidth: '300px', offset: 22 })
    .setLngLat([hike.coord.lng, hike.coord.lat])
    .setHTML(html)
    .addTo(map)

  wireHikeEvents(popup.getElement(), hike)
}

function wireNodeEvents(container, node, ctx) {
  const kids = (ctx.indexes.childrenOf.get(node.id) ?? []).filter(k => k.coord)

  container.querySelectorAll('[data-nav]').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault()
      if (popup) popup.remove()
      closeSheet()
      navigate(`#/node/${a.getAttribute('data-nav')}`)
    })
  })

  container.querySelector('.fit-children')?.addEventListener('click', () => {
    const all = [node, ...kids].map(n => [n.coord.lng, n.coord.lat])
    const bounds = all.reduce((b, ll) => b.extend(ll), new maplibregl.LngLatBounds(all[0], all[0]))
    map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 })
    if (popup) popup.remove()
    closeSheet()
  })

  container.querySelector('.collapse-cluster')?.addEventListener('click', () => {
    expandedParents.delete(node.id)
    renderMarkers(ctx)
    if (popup) popup.remove()
    closeSheet()
  })

  container.querySelector('.delete-discovery')?.addEventListener('click', () => {
    removeDiscovery(node.id, ctx)
    if (popup) popup.remove()
    closeSheet()
  })

  const favBtn = container.querySelector('.fav-toggle')
  favBtn?.addEventListener('click', async () => {
    const nowFav = toggleFavorite(node.id)
    favBtn.classList.toggle('is-fav', nowFav)
    favBtn.setAttribute('aria-pressed', String(nowFav))
    favBtn.innerHTML = nowFav ? icon('heart-filled') : icon('heart')
    favBtn.title = nowFav ? 'Uit favorieten halen' : 'Aan favorieten toevoegen'
    try { await setState({ favorites: { [node.id]: nowFav ? true : null } }) }
    catch (e) { console.warn('favoriet niet opgeslagen', e) }
  })
}

function openPopup(node, ctx) {
  if (popup) popup.remove()
  closeSheet()

  const cat = ctx.indexes.categoryById.get(node.category)
  const kids = (ctx.indexes.childrenOf.get(node.id) ?? []).filter(k => k.coord)
  const isFav = !!getStore().game.favorites[node.id]
  const zoom = map.getZoom()
  const isManuallyExpanded = expandedParents.has(node.id)
  const isExpanded = isManuallyExpanded || zoom >= EXPAND_ZOOM
  const canManuallyCollapse = isManuallyExpanded && zoom < EXPAND_ZOOM

  const clusterHint = kids.length > 0 ? `
    <div class="pin-popup-children">
      <div class="pin-cat">${isExpanded ? `Inhoud zichtbaar (${kids.length})` : `Bevat ${kids.length} plek${kids.length === 1 ? '' : 'ken'}`}</div>
      <div class="cluster-actions">
        <button class="btn secondary fit-children" data-fit="${escapeHtml(node.id)}">${icon('plus')} Zoom in en toon attracties</button>
        ${canManuallyCollapse
          ? `<button class="btn secondary collapse-cluster">${icon('minus')} Inhoud verbergen</button>`
          : ''}
      </div>
      <ul>
        ${kids.slice(0, 8).map(k => `<li><a href="#/node/${k.id}" data-nav="${escapeHtml(k.id)}">${escapeHtml(k.name)}</a></li>`).join('')}
      </ul>
      ${kids.length > 8 ? `<p class="more-kids">+${kids.length - 8} meer in de detailpagina</p>` : ''}
    </div>` : ''

  const discoveryBadge = node.isDiscovery ? `<span class="discovery-badge" title="AI-ontdekt">${icon('sparkles')}</span>` : ''
  const deleteBtn = node.isDiscovery ? `<button class="btn-icon delete-discovery" title="Verwijderen">${icon('x')}</button>` : ''

  const html = `
    <div class="pin-popup ${node.kind === 'town' ? 'is-town' : ''}">
      <div class="popup-header">
        <div class="popup-header-text">
          <div class="pin-cat">${discoveryBadge}${escapeHtml(cat?.label ?? '')}</div>
          <h3><a href="#/node/${node.id}" data-nav="${escapeHtml(node.id)}">${escapeHtml(node.name)}</a></h3>
        </div>
        ${deleteBtn}
        <button class="fav-toggle ${isFav ? 'is-fav' : ''}" data-fav="${escapeHtml(node.id)}" aria-pressed="${isFav}" title="${isFav ? 'Uit favorieten halen' : 'Aan favorieten toevoegen'}">
          ${isFav ? icon('heart-filled') : icon('heart')}
        </button>
      </div>
      ${node.blurb ? `<p>${escapeHtml(node.blurb)}</p>` : ''}
      <div class="popup-thumb" data-popup-thumb="${escapeHtml(node.id)}"></div>
      <a class="btn primary popup-show-btn" href="#/node/${node.id}" data-nav="${escapeHtml(node.id)}">${icon('chevron-right')} Bekijk</a>
      ${clusterHint}
    </div>
  `

  const lngLat = [node.coord.lng, node.coord.lat]

  if (isMobile()) {
    const body = showSheet(html, lngLat)
    wireNodeEvents(body, node, ctx)
    loadPopupThumb(body, node, ctx)
    return
  }

  popup = new maplibregl.Popup({ closeButton: true, maxWidth: '320px', offset: 22, anchor: 'bottom' })
    .setLngLat(lngLat)
    .setHTML(html)
    .addTo(map)

  setTimeout(() => {
    const el = popup.getElement()
    if (!el) return
    const rect = el.getBoundingClientRect()
    const mapRect = mapContainer.getBoundingClientRect()
    const padTop = 60
    const padBot = 30
    let dy = 0
    if (rect.top < mapRect.top + padTop) {
      dy = rect.top - mapRect.top - padTop
    } else if (rect.bottom > mapRect.bottom - padBot) {
      dy = rect.bottom - mapRect.bottom + padBot
    }
    if (Math.abs(dy) > 5) {
      map.panBy([0, dy], { duration: 300 })
    }
  }, 80)

  wireNodeEvents(popup.getElement(), node, ctx)
  loadPopupThumb(popup.getElement(), node, ctx)
}

function loadPopupThumb(container, node, ctx) {
  const slot = container.querySelector(`[data-popup-thumb="${CSS.escape(node.id)}"]`)
  if (!slot) return
  fetchImage('node', node, ctx).then(url => {
    if (!url || !slot.isConnected) return
    slot.innerHTML = `<a href="#/node/${escapeHtml(node.id)}" data-nav="${escapeHtml(node.id)}"><img src="${escapeHtml(url)}" alt="${escapeHtml(node.name)}" loading="lazy"/></a>`
    slot.querySelector('[data-nav]')?.addEventListener('click', (e) => {
      e.preventDefault()
      if (popup) popup.remove()
      closeSheet()
      navigate(`#/node/${node.id}`)
    })
  })
}

// ---------- Discovery (AI zoek meer) ----------

const DISCOVERY_CAT_MAP = {
  natuur: 'natuur', cultuur: 'gebouw', eten: 'eten', curiositeit: 'natuur',
  gebouw: 'gebouw', uitzicht: 'uitzicht', religieus: 'religieus',
  kuur: 'kuur', industrieel: 'industrieel', kasteel: 'kasteel', plein: 'plein',
}

let discoveryCounter = 0

function discoveryToNode(place, ctx) {
  if (place.id?.startsWith('discovery-')) return place
  const cat = DISCOVERY_CAT_MAP[place.category] || 'natuur'
  return {
    id: `discovery-${++discoveryCounter}`,
    name: place.name,
    blurb: place.description,
    coord: place.coord,
    category: cat,
    visit: 'tussenstop',
    kind: 'poi',
    isDiscovery: true,
    sections: [],
  }
}

function injectDiscoveries(nodes, ctx) {
  for (const n of nodes) {
    if (ctx.indexes.nodesById.has(n.id)) continue
    ctx.data.nodes.push(n)
    ctx.indexes.nodesById.set(n.id, n)
  }
}

function removeDiscovery(nodeId, ctx) {
  ctx.data.nodes = ctx.data.nodes.filter(n => n.id !== nodeId)
  ctx.indexes.nodesById.delete(nodeId)
  const entry = markerById.get(nodeId)
  if (entry) { entry.marker.remove(); markerById.delete(nodeId) }
  const store = getStore()
  const updated = (store.game.discoveries || []).filter(d => d.id !== nodeId)
  setGameState({ discoveries: updated })
  setState({ discoveries: updated }).catch(() => {})
  renderMarkers(ctx)
}

function clearDiscoveryMarkers() {
  for (const m of discoveryMarkers) m.marker.remove()
  discoveryMarkers = []
}

function renderDiscoveryMarkers(nodes, ctx) {
  // Discovery nodes are now regular nodes rendered by renderMarkers.
  // This function just triggers the appear animation on new discovery markers.
  requestAnimationFrame(() => {
    for (const n of nodes) {
      const entry = markerById.get(n.id)
      if (entry && !entry.el.classList.contains('appeared')) {
        entry.el.classList.add('appearing')
        entry.el.addEventListener('animationend', () => {
          entry.el.classList.remove('appearing')
          entry.el.classList.add('appeared')
        }, { once: true })
      }
    }
  })
}

async function onDiscoverClick(btn, ctx) {
  if (btn.disabled) return
  btn.disabled = true
  const origHtml = btn.innerHTML
  btn.classList.add('expanded')
  btn.innerHTML = `<span class="spinner"></span><span>Zoeken in dit gebied…</span>`

  try {
    const center = map.getCenter()
    const bounds = map.getBounds()
    const ne = bounds.getNorthEast()
    const radiusKm = Math.round(center.distanceTo(ne) / 1000)

    const result = await discoverPlaces(center.lat, center.lng, Math.min(radiusKm, 50))
    const places = result.places || []

    if (places.length) {
      const nodes = places.map(p => discoveryToNode(p, ctx))
      injectDiscoveries(nodes, ctx)
      renderDiscoveryMarkers(nodes, ctx)

      const store = getStore()
      const merged = [...(store.game.discoveries || []), ...nodes].slice(-50)
      setGameState({ discoveries: merged })
      setState({ discoveries: merged }).catch(() => {})
      renderMarkers(ctx)

      btn.innerHTML = `${icon('sparkles')}<span>${places.length} plekken gevonden</span>`
      setTimeout(() => { btn.classList.remove('expanded'); btn.innerHTML = origHtml }, 2500)
    } else {
      btn.innerHTML = `${icon('sparkles')}<span>Niets gevonden</span>`
      setTimeout(() => { btn.classList.remove('expanded'); btn.innerHTML = origHtml }, 2000)
    }
  } catch (e) {
    console.warn('discover failed', e)
    btn.innerHTML = `${icon('sparkles')}<span>Fout — probeer opnieuw</span>`
    setTimeout(() => { btn.classList.remove('expanded'); btn.innerHTML = origHtml }, 2000)
  } finally {
    btn.disabled = false
  }
}

function restoreDiscoveryMarkers(ctx) {
  const discoveries = getStore().game.discoveries || []
  if (!discoveries.length || !map) return
  discoveryCounter = Math.max(discoveryCounter, ...discoveries.map(d => {
    const m = d.id?.match(/discovery-(\d+)/)
    return m ? Number(m[1]) : 0
  }))
  for (const d of discoveries) {
    if (!d.id) d.id = `discovery-${++discoveryCounter}`
    d.isDiscovery = true
  }
  injectDiscoveries(discoveries, ctx)
  renderMarkers(ctx)
}

// ---------- Publiek ----------
export function renderMap(root, ctx) {
  setTitle('')
  buildWrap()
  if (!wrap.querySelector('.discover-fab')) {
    wrap.insertAdjacentHTML('beforeend',
      `<button class="map-fab discover-fab" id="discover-btn" type="button" title="Zoek meer in dit gebied">${icon('sparkles')}</button>`)
  }
  if (!wrap.querySelector('.map-sheet')) {
    wrap.insertAdjacentHTML('beforeend', `
      <div class="map-sheet">
        <div class="map-sheet-handle"></div>
        <button class="map-sheet-close">${icon('x')}</button>
        <div class="map-sheet-body"></div>
      </div>`)
    sheetEl = wrap.querySelector('.map-sheet')
    sheetEl.querySelector('.map-sheet-close').addEventListener('click', closeSheet)
    initSheetDrag(sheetEl)
  }
  const discoverBtn = wrap.querySelector('#discover-btn')
  if (discoverBtn && !discoverBtn._wired) {
    discoverBtn._wired = true
    discoverBtn.addEventListener('click', () => onDiscoverClick(discoverBtn, ctx))
  }

  if (!wrap.isConnected) root.appendChild(wrap)
  ensureMap(ctx)

  renderControls(ctx)
  renderFilterPanel(ctx)

  const focusNodeId = ctx.route.params.node
  let markersDone = false
  function afterLoad() {
    if (markersDone) return
    markersDone = true
    renderMarkers(ctx)
    restoreDiscoveryMarkers(ctx)
    if (focusNodeId) focusNode(focusNodeId, ctx)
  }

  map.resize()
  if (map.loaded()) {
    afterLoad()
  } else {
    map.once('load', afterLoad)
    map.once('idle', afterLoad)
    setTimeout(afterLoad, 500)
  }

  if (!storeUnsub) {
    storeUnsub = subscribe((reason) => {
      if (!wrap || !wrap.isConnected) return
      if (reason === 'filters' || reason === 'favorites') {
        renderControls(ctx)
        renderFilterPanel(ctx)
        if (map?.loaded()) renderMarkers(ctx)
      }
    })
  }
}

function focusNode(nodeId, ctx) {
  const node = ctx.data.nodes?.find(n => n.id === nodeId)
  if (!node?.coord) return
  map.flyTo({ center: [node.coord.lng, node.coord.lat], zoom: 15, duration: 1000 })
  setTimeout(() => openPopup(node, ctx), 1050)
}

export const mountMap = renderMap

export function unmountMap() {
  if (wrap && wrap.isConnected) wrap.parentNode.removeChild(wrap)
}
